import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Typography, Tag, Button, Popconfirm, Space, Select, Input, DatePicker, Table, Modal, InputNumber, Checkbox, message, type TagTone } from '../../ui';
import { Plus } from 'lucide-react';
import { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { useSearchParams } from 'react-router-dom';
import { usePermission, useAnyPermission } from '../../store/auth';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { TransferFormModal } from './TransferFormModal';
import { formatIst, formatIstDate } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { boxLoosePhrase } from '../../lib/packaging';

const COLOR: Record<string, TagTone> = {
  DRAFT: 'neutral', REQUESTED: 'blue', SOURCE_ACK: 'cyan', PREPARED: 'blue',
  IN_TRANSIT: 'gold', RECEIVED: 'green', PARTIAL: 'red', CANCELLED: 'red', REJECTED: 'red',
};
const STATUSES = ['REQUESTED', 'SOURCE_ACK', 'PREPARED', 'IN_TRANSIT', 'RECEIVED', 'PARTIAL', 'CANCELLED'];

export function TransfersPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [dispatchFor, setDispatchFor] = useState<any | null>(null);     // REQUESTED transfer being dispatched (batch pick)
  const [receiveFor, setReceiveFor] = useState<any | null>(null);       // IN_TRANSIT transfer being received
  const [discrepancyFor, setDiscrepancyFor] = useState<any | null>(null); // PARTIAL transfer to resolve
  // Each manager action is gated by its own permission (or STOCK.TRANSFER for senior roles).
  const canManage = usePermission('STOCK.TRANSFER');            // create / cancel / resolve discrepancy
  const canDispatch = useAnyPermission(['WAREHOUSE.TRANSFER.DISPATCH', 'STOCK.TRANSFER']);
  const canReceive = useAnyPermission(['WAREHOUSE.TRANSFER.RECEIVE', 'STOCK.TRANSFER']);
  const canAct = canDispatch || canReceive || canManage;

  // ── Filters ──────────────────────────────────────────────────────────────
  // Seed the search from ?q= so deep links (e.g. a GRN's STR tag) land pre-filtered to that transfer.
  const [sp] = useSearchParams();
  const [search, setSearch] = useState(sp.get('q') ?? '');
  const [fromWh, setFromWh] = useState<string | undefined>();
  const [toWh, setToWh] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [productId, setProductId] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (search.trim()) p.search = search.trim();
    if (fromWh) p.fromWarehouseId = fromWh;
    if (toWh) p.toWarehouseId = toWh;
    if (status) p.status = status;
    if (productId) p.productId = productId;
    if (range?.[0] && range?.[1]) { p.from = range[0].format('YYYY-MM-DD'); p.to = range[1].format('YYYY-MM-DD'); }
    return p;
  }, [search, fromWh, toWh, status, productId, range]);

  const { data, isLoading } = useQuery({
    queryKey: ['stock-transfers', params],
    queryFn: () => request<any[]>({ url: '/stock-transfers', params }),
    placeholderData: keepPreviousData,
  });
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
  });
  const { data: products } = useQuery({
    queryKey: ['products-list'],
    queryFn: () => request<any>({ url: '/products', params: { pageSize: 500 } }),
  });
  const productList = useMemo(() => products?.data ?? [], [products]);

  const whName = useMemo(() => {
    const m = new Map((warehouses ?? []).map((w: any) => [String(w.id), `${w.code} · ${w.name}`]));
    return (id: any) => m.get(String(id)) ?? id;
  }, [warehouses]);

  const filtersActive = !!(search.trim() || fromWh || toWh || status || productId || range);
  const clearFilters = () => { setSearch(''); setFromWh(undefined); setToWh(undefined); setStatus(undefined); setProductId(undefined); setRange(null); };

  const refresh = () => qc.invalidateQueries({ queryKey: ['stock-transfers'] });
  const onErr = (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed');
  const dispatchMut = useMutation({
    mutationFn: ({ id, allocations }: { id: string; allocations: any[] }) => request({ url: `/stock-transfers/${id}/dispatch`, method: 'POST', data: { allocations } }),
    onSuccess: () => { message.success('Dispatched — stock left the source'); setDispatchFor(null); refresh(); },
    onError: onErr,
  });
  const receiveMut = useMutation({
    mutationFn: ({ id, lines }: { id: string; lines?: any[] }) => request<any>({ url: `/stock-transfers/${id}/receive`, method: 'POST', data: { lines } }),
    onSuccess: (r: any) => { message.success(r?.status === 'PARTIAL' ? 'Received with a discrepancy — an admin will resolve it' : 'Received'); setReceiveFor(null); refresh(); },
    onError: onErr,
  });
  const cancelMut = useMutation({ mutationFn: (id: string) => request({ url: `/stock-transfers/${id}/cancel`, method: 'POST' }), onSuccess: () => { message.success('Cancelled'); refresh(); }, onError: onErr });
  const resolveMut = useMutation({
    mutationFn: ({ id, resolution, notes }: { id: string; resolution: string; notes?: string }) => request({ url: `/stock-transfer-discrepancies/${id}/resolve`, method: 'POST', data: { resolution, notes } }),
    onSuccess: () => { message.success('Discrepancy resolved'); qc.invalidateQueries({ queryKey: ['transfer-discrepancies'] }); refresh(); },
    onError: onErr,
  });

  const cancelBtn = (r: any) => (
    <Popconfirm key="cancel" title="Cancel this transfer?"
      description={r.status === 'IN_TRANSIT' ? 'In-transit stock returns to the source warehouse.' : 'The request will be voided.'}
      okText="Cancel transfer" okButtonProps={{ danger: true }} onConfirm={() => cancelMut.mutate(r.id)}>
      <Button size="sm" variant="danger" loading={cancelMut.isPending}>Cancel</Button>
    </Popconfirm>
  );

  const columns: DataTableColumn<any>[] = [
    { key: 'transferNo', title: 'Transfer No', dataIndex: 'transferNo', sortable: true, alwaysVisible: true },
    { key: 'fromWarehouseId', title: 'From WH', dataIndex: 'fromWarehouseId', sortable: true, render: (v: any) => whName(v) },
    { key: 'toWarehouseId', title: 'To WH', dataIndex: 'toWarehouseId', sortable: true, render: (v: any) => whName(v) },
    { key: 'transferDate', title: 'Date', dataIndex: 'transferDate', sortable: true, render: (v: string) => formatIstDate(v) },
    { key: 'itemCount', title: 'Items', dataIndex: 'itemCount', sortable: true, align: 'right' },
    { key: 'totalQty', title: 'Total Qty', dataIndex: 'totalQty', sortable: true, align: 'right', render: (v: any) => fmtQty(v) },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (s: string) => <Tag tone={COLOR[s]}>{(s ?? '').replace(/_/g, ' ')}</Tag>,
    },
    { key: 'remarks', title: 'Remarks', dataIndex: 'remarks', defaultVisible: false, render: (v: any) => v || '—' },
    {
      key: 'receivedAt', title: 'Received At', dataIndex: 'receivedAt', sortable: true, defaultVisible: false,
      render: (v: string) => formatIst(v),
    },
    {
      key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, defaultVisible: false,
      render: (v: string) => formatIst(v),
    },
    ...(canAct ? [{
      key: 'actions', title: 'Action', alwaysVisible: true, width: 240,
      render: (_: any, r: any) => {
        const s = r.status;
        const primary =
          (['REQUESTED', 'SOURCE_ACK', 'PREPARED'].includes(s) && canDispatch) ? (
            <Button key="disp" size="sm" variant="primary" onClick={() => setDispatchFor(r)}>Dispatch</Button>
          ) : s === 'IN_TRANSIT' && canReceive ? (
            <Button key="recv" size="sm" variant="primary" onClick={() => setReceiveFor(r)}>Receive</Button>
          ) : s === 'PARTIAL' && canManage ? (
            <Button key="disc" size="sm" variant="danger" onClick={() => setDiscrepancyFor(r)}>Resolve discrepancy</Button>
          ) : null;
        const showCancel = canManage && ['REQUESTED', 'SOURCE_ACK', 'PREPARED', 'IN_TRANSIT'].includes(s);
        if (!primary && !showCancel) return null;
        return <Space>{primary}{showCancel ? cancelBtn(r) : null}</Space>;
      },
    } as DataTableColumn<any>] : []),
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Stock Transfers</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canManage && !embedded && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setFormOpen(true)}>
              New Transfer
            </Button>
          )}
        </div>
      </div>
      <Card>
        <DataTable
          storageKey="transfers"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          expandable={{
            rowExpandable: (r: any) => (r.lines?.length ?? 0) > 0,
            expandedRowRender: (r: any) => (
              <Table
                size="small"
                pagination={false}
                rowKey={(l: any, i?: number) => String(l.id ?? i)}
                dataSource={r.lines ?? []}
                columns={[
                  {
                    title: 'Product',
                    render: (_: any, l: any) => (
                      <div style={{ lineHeight: 1.25 }}>
                        <div style={{ fontWeight: 500 }}>{l.productName ?? l.productId}</div>
                        {l.sku && <div style={{ fontSize: 12, color: '#8c8c8c' }}>{l.sku}</div>}
                      </div>
                    ),
                  },
                  { title: 'Batch', dataIndex: 'batchNo', render: (v: any) => v ?? '—' },
                  { title: 'Expiry', dataIndex: 'expDate', render: (v: any) => (v ? formatIstDate(v) : '—') },
                  { title: 'Qty', dataIndex: 'qty', align: 'right' as const, render: (v: any, l: any) => {
                    const phrase = boxLoosePhrase(l.boxQuantity, l.looseUnitQuantity, l.unitsPerBoxSnapshot);
                    return phrase ? <span>{fmtQty(v)}<div style={{ fontSize: 11, color: '#8c8c8c' }}>{phrase}</div></span> : fmtQty(v);
                  } },
                ]}
              />
            ),
          }}
          toolbarLeft={
            <Space wrap>
              <Input placeholder="Transfer no" style={{ width: 150 }}
                value={search} onChange={(e) => setSearch(e.target.value)} />
              <Select placeholder="From WH" allowClear showSearch optionFilterProp="label" style={{ width: 190 }}
                value={fromWh} onChange={setFromWh}
                options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), label: `${w.code} · ${w.name}` }))} />
              <Select placeholder="To WH" allowClear showSearch optionFilterProp="label" style={{ width: 190 }}
                value={toWh} onChange={setToWh}
                options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), label: `${w.code} · ${w.name}` }))} />
              <Select placeholder="Status" allowClear style={{ width: 140 }} value={status} onChange={setStatus}
                options={STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') }))} />
              <Select placeholder="Product" allowClear showSearch optionFilterProp="label" style={{ width: 220 }}
                value={productId} onChange={setProductId}
                options={productList.map((p: any) => ({
                  value: String(p.id),
                  label: `${p.variantLabel ? `${p.name} ${p.variantLabel}` : p.name} · ${p.sku}`,
                }))} />
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => setRange(r as [Dayjs, Dayjs] | null)} />
              {filtersActive && <Button variant="text" onClick={clearFilters}>Clear</Button>}
            </Space>
          }
        />
      </Card>
      <TransferFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      {dispatchFor && (
        <DispatchModal transfer={dispatchFor} loading={dispatchMut.isPending}
          onClose={() => setDispatchFor(null)}
          onSubmit={(allocations) => dispatchMut.mutate({ id: dispatchFor.id, allocations })} />
      )}
      {receiveFor && (
        <ReceiveModal transfer={receiveFor} loading={receiveMut.isPending}
          onClose={() => setReceiveFor(null)}
          onSubmit={(lines) => receiveMut.mutate({ id: receiveFor.id, lines })} />
      )}
      {discrepancyFor && (
        <DiscrepancyModal transfer={discrepancyFor} resolving={resolveMut.isPending}
          onClose={() => setDiscrepancyFor(null)}
          onResolve={(id, resolution, notes) => resolveMut.mutate({ id, resolution, notes })} />
      )}
    </div>
  );
}

// Destination-warehouse receive: per-line good/damaged/batch-mismatch. Short/damage/mismatch
// creates a discrepancy and holds the transfer PARTIAL until an admin resolves it.
function ReceiveModal({ transfer, onClose, onSubmit, loading }: { transfer: any; onClose: () => void; onSubmit: (lines: any[]) => void; loading: boolean }) {
  const lines: any[] = transfer.lines ?? [];
  const [rows, setRows] = useState<Record<string, { received: number; damaged: number; mismatch: boolean }>>(() => {
    const init: Record<string, any> = {};
    for (const l of lines) init[String(l.id)] = { received: Number(l.qty), damaged: 0, mismatch: false };
    return init;
  });
  const set = (id: string, patch: any) => setRows((s) => ({ ...s, [id]: { ...s[id], ...patch } }));
  const anyDiscrepancy = lines.some((l) => {
    const r = rows[String(l.id)]; return !!r && (r.received + r.damaged < Number(l.qty) - 1e-6 || r.damaged > 0 || r.mismatch);
  });
  const submit = () => onSubmit(lines.map((l) => {
    const r = rows[String(l.id)];
    return { transferLineId: String(l.id), receivedQty: r.received, damagedQty: r.damaged, batchMismatch: r.mismatch };
  }));
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Receive transfer ${transfer.transferNo}`} width={780}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant={anyDiscrepancy ? 'danger' : 'primary'} loading={loading} onClick={submit}>{anyDiscrepancy ? 'Receive with discrepancy' : 'Receive in full'}</Button>
      </>}>
      <Typography.Paragraph type="secondary">Enter the quantities actually received. Short, damaged or batch-mismatch lines create a discrepancy for an admin to resolve.</Typography.Paragraph>
      <Table size="small" pagination={false} rowKey={(l: any) => String(l.id)} dataSource={lines}
        columns={[
          { title: 'Product', render: (_: any, l: any) => (
            <div style={{ lineHeight: 1.25 }}>
              <div style={{ fontWeight: 500 }}>{l.productName ?? l.productId}</div>
              {l.batchNo && <div style={{ fontSize: 12, color: '#8c8c8c' }}>Batch {l.batchNo}{l.expDate ? ` · exp ${formatIstDate(l.expDate)}` : ''}</div>}
            </div>
          ) },
          { title: 'Dispatched', dataIndex: 'qty', align: 'right' as const, render: (v: any) => fmtQty(v) },
          { title: 'Received (good)', render: (_: any, l: any) => <InputNumber min={0} max={Number(l.qty)} value={rows[String(l.id)]?.received} onChange={(v) => set(String(l.id), { received: Number(v ?? 0) })} /> },
          { title: 'Damaged', render: (_: any, l: any) => <InputNumber min={0} max={Number(l.qty)} value={rows[String(l.id)]?.damaged} onChange={(v) => set(String(l.id), { damaged: Number(v ?? 0) })} /> },
          { title: 'Batch mismatch', align: 'center' as const, render: (_: any, l: any) => <Checkbox checked={rows[String(l.id)]?.mismatch} onCheckedChange={(checked) => set(String(l.id), { mismatch: checked })} /> },
        ]} />
    </Modal>
  );
}

// Admin resolution of a transfer's open discrepancies (ACCEPT_LOSS or RETURN_TO_SOURCE).
function DiscrepancyModal({ transfer, onClose, onResolve, resolving }: { transfer: any; onClose: () => void; onResolve: (id: string, resolution: string, notes?: string) => void; resolving: boolean }) {
  const { data } = useQuery({
    queryKey: ['transfer-discrepancies', transfer.id],
    queryFn: () => request<any[]>({ url: '/stock-transfer-discrepancies', params: { transferId: transfer.id } }),
  });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const open = (data ?? []).filter((d: any) => d.status === 'OPEN');
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Discrepancies · ${transfer.transferNo}`} width={860}>
      <Table size="small" pagination={false} rowKey="id" dataSource={open} locale={{ emptyText: 'No open discrepancies' }}
        columns={[
          { title: 'Product', render: (_: any, d: any) => d.productName ?? d.productId },
          { title: 'Type', dataIndex: 'type', render: (v: string) => <Tag tone="red">{(v ?? '').replace(/_/g, ' ')}</Tag> },
          { title: 'Expected', dataIndex: 'expectedQty', align: 'right' as const, render: (v: any) => fmtQty(v) },
          { title: 'Received', dataIndex: 'receivedQty', align: 'right' as const, render: (v: any) => fmtQty(v) },
          { title: 'Short', dataIndex: 'shortQty', align: 'right' as const, render: (v: any) => fmtQty(v) },
          { title: 'Damaged', dataIndex: 'damagedQty', align: 'right' as const, render: (v: any) => fmtQty(v) },
          { title: 'Resolve', render: (_: any, d: any) => (
            <Space>
              <Input size="sm" placeholder="Notes" style={{ width: 120 }} value={notes[d.id] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [d.id]: e.target.value }))} />
              <Popconfirm title="Write off the short/damaged qty (a genuine loss)?" onConfirm={() => onResolve(d.id, 'ACCEPT_LOSS', notes[d.id])}>
                <Button size="sm" variant="default" loading={resolving}>Accept loss</Button>
              </Popconfirm>
              <Popconfirm title="Return the short/damaged qty to the source warehouse?" onConfirm={() => onResolve(d.id, 'RETURN_TO_SOURCE', notes[d.id])}>
                <Button size="sm" variant="default" loading={resolving}>Return to source</Button>
              </Popconfirm>
            </Space>
          ) },
        ]} />
    </Modal>
  );
}

// Source-warehouse dispatch: the manager picks which source batch(es) cover each requested product.
// The allocated quantity must equal the requested quantity per product; stock leaves on dispatch.
function DispatchModal({ transfer, onClose, onSubmit, loading }: { transfer: any; onClose: () => void; onSubmit: (allocations: any[]) => void; loading: boolean }) {
  const lines: any[] = transfer.lines ?? [];
  const products = useMemo(() => {
    const m = new Map<string, { productId: string; productName: string; sku: string; requested: number }>();
    for (const l of lines) {
      const k = String(l.productId);
      const e = m.get(k) ?? { productId: k, productName: l.productName ?? String(l.productId), sku: l.sku ?? '', requested: 0 };
      e.requested += Number(l.qty);
      m.set(k, e);
    }
    return [...m.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfer]);

  // alloc: productId -> (batchId -> qty)
  const [alloc, setAlloc] = useState<Record<string, Record<string, number>>>({});
  const setQty = (pid: string, bid: string, qty: number) => setAlloc((s) => ({ ...s, [pid]: { ...(s[pid] ?? {}), [bid]: qty } }));
  const allocatedFor = (pid: string) => Object.values(alloc[pid] ?? {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const allValid = products.every((p) => Math.abs(allocatedFor(p.productId) - p.requested) < 1e-6);

  const submit = () => {
    const allocations: any[] = [];
    for (const p of products) {
      for (const [bid, q] of Object.entries(alloc[p.productId] ?? {})) {
        if (Number(q) > 0) allocations.push({ productId: p.productId, batchId: bid, qty: Number(q) });
      }
    }
    onSubmit(allocations);
  };

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title={`Dispatch transfer ${transfer.transferNo}`} width={840}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={loading} disabled={!allValid} onClick={submit}>Dispatch</Button>
      </>}>
      <Typography.Paragraph type="secondary">
        Pick the source batch(es) for each product — the allocated quantity must equal the requested quantity. Stock leaves the source warehouse on dispatch and the destination creates a GRN on receipt.
      </Typography.Paragraph>
      {products.map((p) => (
        <ProductBatchPicker key={p.productId} warehouseId={transfer.fromWarehouseId} product={p}
          alloc={alloc[p.productId] ?? {}} onQty={(bid, qty) => setQty(p.productId, bid, qty)} allocated={allocatedFor(p.productId)} />
      ))}
    </Modal>
  );
}

function ProductBatchPicker({ warehouseId, product, alloc, onQty, allocated }: {
  warehouseId: string; product: { productId: string; productName: string; sku: string; requested: number };
  alloc: Record<string, number>; onQty: (batchId: string, qty: number) => void; allocated: number;
}) {
  const { data: batches } = useQuery({
    queryKey: ['transfer-source-batches', warehouseId, product.productId],
    queryFn: () => request<any[]>({ url: '/transfer-source-batches', params: { warehouseId, productId: product.productId } }),
    enabled: !!warehouseId && !!product.productId,
  });
  const list = batches ?? [];
  const over = allocated > product.requested + 1e-6;
  const short = allocated < product.requested - 1e-6;
  return (
    <Card className="mb-2.5"
      title={<Space>{product.productName}{product.sku && <Tag>{product.sku}</Tag>}</Space>}
      extra={<Tag tone={over ? 'red' : short ? 'gold' : 'green'}>{fmtQty(allocated)} / {fmtQty(product.requested)}</Tag>}>
      <Table size="small" pagination={false} rowKey="batchId" dataSource={list}
        locale={{ emptyText: 'No available stock for this product in the source warehouse' }}
        columns={[
          { title: 'Batch', dataIndex: 'batchNo' },
          { title: 'Expiry', dataIndex: 'expDate', render: (v: any) => (v ? formatIstDate(v) : '—') },
          { title: 'Available', dataIndex: 'available', align: 'right' as const, render: (v: any) => fmtQty(v) },
          {
            title: 'Allocate', align: 'right' as const,
            render: (_: any, b: any) => (
              <InputNumber min={0} max={Number(b.available)} value={alloc[b.batchId] ?? 0}
                onChange={(v) => onQty(String(b.batchId), Number(v ?? 0))} />
            ),
          },
        ]} />
    </Card>
  );
}
